/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Component, Input, Output, EventEmitter } from "@angular/core";
import { CommonModule } from "@angular/common";
import { CardModule } from "primeng/card";
import { TagModule } from "primeng/tag";
import { BadgeModule } from "primeng/badge";
import { TooltipModule } from "primeng/tooltip";
import { ButtonModule } from "primeng/button";
import { ScheduledOrder } from "../../../../core/models/scheduled-order.interface";
import { ScheduledOrderItem } from "../../../../core/models/scheduled-order-item.interface";
import { ScheduledOrderTotals } from "../../../../core/models/scheduled-order-totals.interface";

/**
 * Component to display a summary of a scheduled order
 * Shows key information in a compact, readable format with clickable navigation
 * Only displayed when editing an existing scheduled order
 */
@Component({
  selector: "app-scheduled-order-summary",
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    TagModule,
    BadgeModule,
    TooltipModule,
    ButtonModule,
  ],
  templateUrl: "./scheduled-order-summary.component.html",
  styleUrl: "./scheduled-order-summary.component.scss",
})
export class ScheduledOrderSummaryComponent {
  @Input() scheduledOrder?: ScheduledOrder;
  @Input() totals?: ScheduledOrderTotals;

  // Expose Math to template
  Math = Math;

  // Navigation events
  @Output() navigateToCustomer = new EventEmitter<void>();
  @Output() navigateToProducts = new EventEmitter<void>();
  @Output() navigateToSchedule = new EventEmitter<void>();
  @Output() navigateToCoupons = new EventEmitter<void>();
  @Output() navigateToValidation = new EventEmitter<void>();
  @Output() openTotalsPopover = new EventEmitter<MouseEvent>();

  /**
   * Get customer email or placeholder
   */
  getCustomerEmail(): string {
    return this.scheduledOrder?.Customer?.Email || "—";
  }

  /**
   * Get formatted created date or placeholder
   */
  getCreatedDate(): string {
    if (!this.scheduledOrder?.CreatedUtc) {
      return "—";
    }
    return new Date(this.scheduledOrder.CreatedUtc).toLocaleString();
  }

  /**
   * Get formatted next occurrence date or placeholder
   */
  getNextOccurrenceDate(): string {
    if (!this.scheduledOrder?.NextOccurrenceUtc) {
      return "Not scheduled";
    }
    return new Date(this.scheduledOrder.NextOccurrenceUtc).toLocaleString();
  }

  /**
   * Get total cycles or placeholder
   */
  getTotalCycles(): string {
    if (!this.scheduledOrder?.Cycles) {
      return "0";
    }
    return this.scheduledOrder.Cycles.toString();
  }

  /**
   * Get formatted lifetime value or placeholder
   * Uses customer's lifetime value if available, otherwise scheduled order's lifetime value
   */
  getLifetimeValue(): string {
    // Try customer's lifetime value first (more accurate)
    const lifetimeValue =
      this.scheduledOrder?.Customer?.LifetimeValue ||
      this.scheduledOrder?.LifetimeValue;

    if (!lifetimeValue || lifetimeValue <= 0) {
      return "$0.00";
    }

    const currency = this.scheduledOrder?.CurrencyIso || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(lifetimeValue);
  }

  /**
   * Get last processing cycle or placeholder
   */
  getLastProcessingCycle(): string {
    if (!this.scheduledOrder?.LastProcessingCycle) {
      return "None";
    }
    return `#${this.scheduledOrder.LastProcessingCycle.Id}`;
  }

  /**
   * Get status
   */
  getStatus(): string {
    return this.scheduledOrder?.Status || "Unknown";
  }

  /**
   * Get status severity for tag
   */
  getStatusSeverity(): "success" | "danger" | "warning" | "info" {
    switch (this.scheduledOrder?.Status) {
      case "Active":
        return "success";
      case "Failed":
        return "danger";
      case "Paused":
        return "warning";
      default:
        return "info";
    }
  }

  /**
   * Get only enabled (non-disabled) items
   */
  getEnabledItems() {
    return (
      this.scheduledOrder?.ScheduledOrderItems?.filter(
        (item) => !item.Disabled,
      ) || []
    );
  }

  /**
   * Get disabled items
   */
  getDisabledItems() {
    return (
      this.scheduledOrder?.ScheduledOrderItems?.filter(
        (item) => item.Disabled,
      ) || []
    );
  }

  /**
   * Get product count for display (only enabled items)
   */
  getProductCount(): number {
    return this.getEnabledItems().length;
  }

  /**
   * Get disabled product count
   */
  getDisabledProductCount(): number {
    return this.getDisabledItems().length;
  }

  /**
   * Check if product is enabled for processing scheduled orders
   * Uses Availability array to check for ProcessScheduledOrder capability
   */
  private productIsProcessing(product: { Availability?: string }): boolean {
    return (
      product.Availability !== undefined &&
      product.Availability.indexOf("ProcessScheduledOrder") > -1
    );
  }

  /**
   * Check if product is in stock
   * Uses StockLevel property for availability check
   */
  private productIsInStock(product: { StockLevel?: string }): boolean {
    return (
      product.StockLevel === "Undefined" || product.StockLevel === "InStock"
    );
  }

  /**
   * Check if item is disabled due to backorder restrictions
   */
  private disabledForBackorders(
    item: ScheduledOrderItem,
    product: { BackordersAllowed?: boolean; Stock?: number },
  ): boolean {
    return (
      product.BackordersAllowed !== undefined &&
      product.BackordersAllowed !== null &&
      !product.BackordersAllowed &&
      product.Stock !== undefined &&
      product.Stock !== null &&
      (item.Quantity ?? 0) > product.Stock
    );
  }

  /**
   * Get the reason why an item is disabled
   * Matches logic from old QMC (lines 404-424)
   */
  getDisabledReason(item: ScheduledOrderItem): string {
    const product = item.Product;
    if (!product) return "Product information unavailable";

    // Check various disabled conditions in priority order
    // Backorders not allowed and insufficient stock
    if (this.disabledForBackorders(item, product)) {
      return "Backorders not allowed and quantity exceeds stock. Item will be enabled when stock is sufficient.";
    }

    // Inactive product
    if (product.Active === false) {
      return "Product is inactive. Item will be enabled when product becomes active.";
    }

    // Invalid product
    if (product.Valid === false) {
      return "Product is invalid. Item will be enabled when product becomes valid.";
    }

    // Out of stock
    if (!this.productIsInStock(product)) {
      return "This item is Out of Stock and will not process until it is In Stock again. Out of Stock items do not count towards the subtotal value of the Scheduled Order.";
    }

    // Not enabled for processing
    if (!this.productIsProcessing(product)) {
      return "Not enabled for processing. Disabled items do not count towards the subtotal.";
    }

    return "Item is disabled for processing";
  }

  /**
   * Get appropriate icon for disabled item
   * Matches logic from old QMC
   */
  getDisabledIcon(item: ScheduledOrderItem): string {
    const product = item.Product;
    if (!product) return "pi-exclamation-triangle";

    // Not enabled for processing -> sync icon
    if (!this.productIsProcessing(product)) {
      return "pi-sync";
    }

    // All other cases -> ban icon (do_not_disturb equivalent)
    return "pi-ban";
  }

  /**
   * Handle row click - navigate to Products step
   */
  onRowClick(): void {
    this.navigateToProducts.emit();
  }

  /**
   * Get products summary text (names and quantities) - only enabled items
   */
  getProductsSummary(): string {
    const items = this.getEnabledItems();
    if (items.length === 0) {
      return "No products";
    }

    if (items.length === 1) {
      const item = items[0];
      const name = item.Product?.Title || `Product #${item.ProductId}`;
      return `${name} (${item.Quantity})`;
    }

    // Show first item and count of others
    const firstItem = items[0];
    const firstName =
      firstItem.Product?.Title || `Product #${firstItem.ProductId}`;
    return `${firstName} (${firstItem.Quantity}) + ${items.length - 1} more`;
  }

  /**
   * Handle customer card click
   */
  onCustomerClick(): void {
    this.navigateToCustomer.emit();
  }

  /**
   * Handle products card click
   */
  onProductsClick(): void {
    this.navigateToProducts.emit();
  }

  /**
   * Handle schedule card click (next occurrence)
   */
  onScheduleClick(): void {
    this.navigateToSchedule.emit();
  }

  /**
   * Handle coupons card click
   */
  onCouponsClick(): void {
    this.navigateToCoupons.emit();
  }

  /**
   * Handle notes card click
   */
  onNotesClick(): void {
    this.navigateToCoupons.emit();
  }

  /**
   * Handle validation card click
   */
  onValidationClick(): void {
    this.navigateToValidation.emit();
  }

  /**
   * Handle Order Total $ icon click to open totals popover
   */
  onTotalsIconClick(event: MouseEvent): void {
    event.stopPropagation(); // Prevent row click
    this.openTotalsPopover.emit(event);
  }

  /**
   * Get coupons as comma-separated string
   */
  getCoupons(): string {
    return this.scheduledOrder?.Coupons || "";
  }

  /**
   * Check if order has coupons
   */
  hasCoupons(): boolean {
    return (
      !!this.scheduledOrder?.Coupons &&
      this.scheduledOrder.Coupons.trim().length > 0
    );
  }

  /**
   * Get coupon count
   */
  getCouponCount(): number {
    if (!this.hasCoupons()) return 0;
    return this.scheduledOrder!.Coupons.split(",").filter((c) => c.trim())
      .length;
  }

  /**
   * Get notes
   */
  getNotes(): string {
    return this.scheduledOrder?.Note || "";
  }

  /**
   * Check if order has notes
   */
  hasNotes(): boolean {
    return (
      !!this.scheduledOrder?.Note && this.scheduledOrder.Note.trim().length > 0
    );
  }

  /**
   * Get validation status
   */
  getValidationStatus(): string {
    if (!this.scheduledOrder?.ValidationResult) {
      return "Not validated";
    }
    return this.scheduledOrder.ValidationResult.Succeeded
      ? "Valid"
      : "Has Issues";
  }

  /**
   * Get validation severity
   */
  getValidationSeverity(): "success" | "danger" | "warning" | "info" {
    if (!this.scheduledOrder?.ValidationResult) {
      return "info";
    }
    return this.scheduledOrder.ValidationResult.Succeeded
      ? "success"
      : "danger";
  }

  /**
   * Get validation error count
   */
  getValidationErrorCount(): number {
    return this.scheduledOrder?.ValidationResult?.ErrorItems?.length || 0;
  }

  /**
   * Get formatted price
   */
  formatPrice(price?: number): string {
    if (price === undefined || price === null) {
      return "$0.00";
    }
    const currency = this.scheduledOrder?.CurrencyIso || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(price);
  }

  /**
   * Get items total at regular price (before discount)
   * Uses API totals when available for consistency
   */
  getItemsTotalBeforeDiscount(): string {
    // Use API totals for consistency with Order Total Summary
    if (this.totals?.ItemsTotals?.SubTotal !== undefined) {
      return this.formatPrice(this.totals.ItemsTotals.SubTotal);
    }

    // Fallback: calculate from item prices
    const items = this.getEnabledItems();
    const total = items.reduce((sum, item) => {
      const regularPrice = item.Price ?? 0;
      return sum + regularPrice * (item.Quantity ?? 0);
    }, 0);
    return this.formatPrice(total);
  }

  /**
   * Get price discount from sale prices (as formatted string)
   * Uses API totals when available for consistency
   */
  getPriceDiscount(): string {
    // Use API totals for consistency with Order Total Summary
    if (this.totals?.ItemsTotals?.DiscountTotal !== undefined) {
      return this.formatPrice(-this.totals.ItemsTotals.DiscountTotal);
    }

    // Fallback: calculate from item prices
    const items = this.getEnabledItems();
    const discount = items.reduce((sum, item) => {
      if (item.SalePrice !== undefined && item.SalePrice !== null) {
        const regularPrice = item.Price ?? 0;
        const savings = (regularPrice - item.SalePrice) * (item.Quantity ?? 0);
        return sum + savings;
      }
      return sum;
    }, 0);
    // Return as negative value
    return this.formatPrice(-discount);
  }

  /**
   * Check if there are any price discounts
   * Uses API totals when available
   */
  hasPriceDiscounts(): boolean {
    // Use API totals when available
    if (this.totals?.ItemsTotals?.DiscountTotal !== undefined) {
      return this.totals.ItemsTotals.DiscountTotal > 0;
    }

    // Fallback: check items for sale prices
    const items = this.getEnabledItems();
    return items.some(
      (item) => item.SalePrice !== undefined && item.SalePrice !== null,
    );
  }

  /**
   * Get total price for all enabled items only (after discounts)
   * Uses API totals when available for consistency
   */
  getTotalPrice(): string {
    // Use API totals for consistency with Order Total Summary
    if (this.totals?.SubTotal !== undefined) {
      return this.formatPrice(this.totals.SubTotal);
    }

    // Fallback: calculate from item prices (less accurate)
    const items = this.getEnabledItems();
    const total = items.reduce((sum, item) => {
      const price = item.SalePrice ?? item.Price ?? 0;
      return sum + price * (item.Quantity ?? 0);
    }, 0);
    return this.formatPrice(total);
  }

  /**
   * Check if there are coupon discounts from totals
   */
  hasCouponDiscounts(): boolean {
    const coupons = this.totals?.ItemsTotals?.CouponDiscounts || [];
    return coupons.length > 0;
  }

  /**
   * Get coupon discounts from totals
   */
  getCouponDiscounts(): { Description: string; Amount: number }[] {
    return this.totals?.ItemsTotals?.CouponDiscounts || [];
  }

  /**
   * Get total coupon discount amount
   */
  getTotalCouponDiscount(): number {
    const coupons = this.getCouponDiscounts();
    return coupons.reduce((sum, coupon) => sum + Math.abs(coupon.Amount), 0);
  }

  /**
   * Get order subtotal (after price discounts and coupon discounts)
   */
  getOrderSubtotal(): string {
    return this.formatPrice(this.totals?.SubTotal || 0);
  }

  /**
   * Get order total (including shipping and tax)
   */
  getOrderTotal(): string {
    return this.formatPrice(this.totals?.Total || 0);
  }

  /**
   * Check if there's shipping or tax to show the final total row
   * Only show if totals exist and there's a difference between subtotal and total
   */
  hasShippingOrTax(): boolean {
    if (!this.totals) return false;
    const subtotal = this.totals.SubTotal || 0;
    const total = this.totals.Total || 0;
    // Show final total row if there's any difference (shipping, tax, etc.)
    return Math.abs(total - subtotal) > 0.001;
  }
}
